import { ApplicationInitStatus, Injectable, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { AnalyticsService } from '@lite-workspace/shared/services/analytics';
import { inClientZone } from './client-session';
import { sessionKnown } from './project-role';
import { IdentityStore, type Contact, type UserProfile } from '@lite-workspace/shared/identity';

interface AnalyticsSegments {
  projectsCount: number;
  totalAlbums: number;
  completedProjects: number;
}

// A hung segments request must not strand identify() — and, for an internal user, its exclusion.
const ANALYTICS_SEGMENTS_TIMEOUT_MS = 5000;

const CONTACT_PROFILE_PROPERTY_BY_NAME: Partial<Record<Contact['name'], 'phoneNumber' | 'whatsappPhone'>> = {
  phone: 'phoneNumber',
  whatsapp: 'whatsappPhone',
};

function contactProfileProperties(contacts: readonly Contact[]): { phoneNumber?: string; whatsappPhone?: string } {
  const properties: { phoneNumber?: string; whatsappPhone?: string } = {};
  for (const contact of contacts) {
    const key = CONTACT_PROFILE_PROPERTY_BY_NAME[contact.name];
    if (!key) continue;
    const data = contact.data?.trim();
    if (data && data.toLowerCase() !== 'null' && data.toLowerCase() !== 'undefined') properties[key] = data;
  }

  return properties;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsIdentityService {
  private readonly analytics = inject(AnalyticsService);
  private readonly identity = inject(IdentityStore);
  private readonly http = inject(HttpClient);
  private identifiedProfileId: string | null = null;

  constructor() {
    // Set at construction, not inside identify: events fired on profile load already need it.
    this.analytics.setGlobalProperties({ product: 'fotoclass' });
    // initAuthMode() is a sibling app initializer: until it settles, isBffMode() still reads false and
    // a BFF session would look anonymous.
    void inject(ApplicationInitStatus).donePromise.then(() => this.classifySession());

    effect(() => {
      const profile = this.identity.profile();
      if (!profile) return;
      // The student zone never identifies anyone: whoever loaded the profile there, it only decides the exclusion.
      if (inClientZone()) {
        this.analytics.excludeIfInternal(profile.email);
        return;
      }

      if (profile.id === this.identifiedProfileId) return;
      this.identifiedProfileId = profile.id;
      void this.identifyWithSegments(profile);
    });
  }

  private classifySession(): void {
    const signedIn = sessionKnown();
    if (signedIn && !inClientZone()) {
      // Signed-in sessions stay queued until identify() classifies them (internal ones never transmit).
      // Routes that never mount the shell header (editor/preview/onboarding) never call loadProfile()
      // on their own — without this, identify() would silently never fire for those sessions.
      if (!this.identity.profile()) this.identity.loadProfile();
      return;
    }

    // Visitors (public pages, the student zone) transmit at once.
    this.analytics.openAnonymousTransmission();
    // A live session in the student zone is classified meanwhile: an internal profile stops what follows.
    if (signedIn && !this.identity.profile()) this.identity.loadProfile();
  }

  private async identifyWithSegments(profile: UserProfile): Promise<void> {
    // Segments are best-effort: a failed, slow, or hung aggregate must never block or lose identify itself.
    const segments: AnalyticsSegments | null = await firstValueFrom(
      this.http.get<AnalyticsSegments>('/api/projects/analytics-segments').pipe(timeout(ANALYTICS_SEGMENTS_TIMEOUT_MS)),
    ).catch(() => null);

    this.analytics.identify({
      profileId: profile.id,
      email: profile.email ?? undefined,
      firstName: profile.firstName ?? undefined,
      lastName: profile.lastName ?? undefined,
      ...contactProfileProperties(profile.contacts ?? []),
      properties: {
        app: 'fotoclass-app',
        ...(segments
          ? {
              projects_count: segments.projectsCount,
              total_albums: segments.totalAlbums,
              completed_projects: segments.completedProjects,
            }
          : {}),
      },
    });
  }
}
